/**
 * analytics-shared/fixtures/reportObjects.js
 *
 * Object & field catalog used by the report builder when no backend is
 * available. Mirrors the shape the future GET /metadata/objects endpoint
 * will return, so wiring the API later is a one-line swap inside
 * useFieldCatalog.
 */
import {
  Users, Contact, Building2, Handshake, CheckSquare, Phone,
  CalendarDays, Receipt, LifeBuoy, Megaphone,
} from 'lucide-react';
import { OBJECT_CATEGORIES, FIELD_CATEGORY_LABELS } from '../constants';

// Re-exported for back-compat with code that imported these from this file
// before the canonical home moved to ../constants.
export { OBJECT_CATEGORIES, FIELD_CATEGORY_LABELS };

export const OWNERS = [
  'Rahul Sharma', 'Priya Patel', 'Amit Kumar', 'Sneha Reddy',
  'Vikram Singh', 'Anjali Mehta', 'Karan Malhotra',
];
export const COMPANIES = [
  'Acme Corp', 'Globex Inc', 'Initech', 'Umbrella Co', 'Stark Industries',
  'Wayne Enterprises', 'Soylent Corp', 'Hooli', 'Pied Piper', 'Massive Dynamic',
  'Vandelay Industries', 'Wonka Industries',
];
const SOURCES = ['Website', 'Referral', 'Cold Call', 'Email Campaign', 'Event', 'Partner', 'Social Media'];
const INDUSTRIES = ['Technology', 'Finance', 'Healthcare', 'Manufacturing', 'Retail', 'Education', 'Real Estate'];
const REGIONS = ['North', 'South', 'East', 'West'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

const f = (key, label, type, fieldCategory = 'standard', extra = {}) =>
  ({ key, label, type, fieldCategory, ...extra });

const ownerField = () => f('owner', 'Owner', 'user', 'standard', { values: OWNERS });
const createdField = () => f('created_date', 'Created Date', 'date', 'standard');
const activityField = () => f('last_activity', 'Last Activity', 'date', 'standard');

export const REPORT_OBJECTS = [
  {
    key: 'deals', label: 'Deals', icon: Handshake, category: 'Sales',
    description: 'Opportunities, pipeline value, forecast and win/loss analysis.',
    recentlyUsed: true, nameField: 'deal_name',
    fields: [
      f('deal_name', 'Deal Name', 'text', 'standard'),
      f('amount', 'Deal Amount', 'currency', 'standard', { min: 5000, max: 250000 }),
      f('stage', 'Stage', 'select', 'standard', { values: ['Qualification', 'Needs Analysis', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'] }),
      ownerField(),
      f('company', 'Company', 'text', 'related', { values: COMPANIES }),
      f('pipeline', 'Pipeline', 'select', 'standard', { values: ['Sales Pipeline', 'Enterprise Pipeline', 'SMB Pipeline'] }),
      f('lead_source', 'Lead Source', 'select', 'standard', { values: SOURCES }),
      f('type', 'Deal Type', 'select', 'standard', { values: ['New Business', 'Existing Business', 'Renewal'] }),
      f('probability', 'Probability', 'percent', 'standard', { min: 5, max: 95 }),
      f('forecast_revenue', 'Forecast Revenue', 'currency', 'formula', { min: 1000, max: 200000 }),
      f('region', 'Region', 'select', 'custom', { values: REGIONS }),
      f('priority', 'Priority', 'select', 'custom', { values: PRIORITIES }),
      f('close_date', 'Close Date', 'date', 'standard'),
      createdField(), activityField(),
    ],
  },
  {
    key: 'leads', label: 'Leads', icon: Users, category: 'CRM',
    description: 'Inbound and outbound leads with source, status and rating.',
    recentlyUsed: true, nameField: 'lead_name',
    fields: [
      f('lead_name', 'Lead Name', 'text', 'standard'),
      f('company', 'Company', 'text', 'standard', { values: COMPANIES }),
      f('lead_source', 'Lead Source', 'select', 'standard', { values: SOURCES }),
      f('status', 'Status', 'select', 'standard', { values: ['New', 'Working', 'Nurturing', 'Qualified', 'Unqualified'] }),
      f('rating', 'Rating', 'select', 'standard', { values: ['Hot', 'Warm', 'Cold'] }),
      ownerField(),
      f('estimated_value', 'Estimated Value', 'currency', 'standard', { min: 1000, max: 80000 }),
      f('industry', 'Industry', 'select', 'standard', { values: INDUSTRIES }),
      f('score', 'Lead Score', 'number', 'formula', { min: 0, max: 100 }),
      f('region', 'Region', 'select', 'custom', { values: REGIONS }),
      createdField(), activityField(),
    ],
  },
  {
    key: 'contacts', label: 'Contacts', icon: Contact, category: 'CRM',
    description: 'People records linked to companies and deals.', nameField: 'contact_name',
    fields: [
      f('contact_name', 'Contact Name', 'text', 'standard'),
      f('company', 'Company', 'text', 'related', { values: COMPANIES }),
      f('title', 'Job Title', 'select', 'standard', { values: ['CEO', 'CTO', 'VP Sales', 'Manager', 'Director', 'Analyst'] }),
      ownerField(),
      f('lead_source', 'Lead Source', 'select', 'standard', { values: SOURCES }),
      f('industry', 'Industry', 'select', 'related', { values: INDUSTRIES }),
      f('region', 'Region', 'select', 'custom', { values: REGIONS }),
      createdField(), activityField(),
    ],
  },
  {
    key: 'companies', label: 'Companies', icon: Building2, category: 'CRM',
    description: 'Accounts with revenue, size and industry segmentation.', nameField: 'company_name',
    fields: [
      f('company_name', 'Company Name', 'text', 'standard', { values: COMPANIES }),
      f('industry', 'Industry', 'select', 'standard', { values: INDUSTRIES }),
      f('annual_revenue', 'Annual Revenue', 'currency', 'standard', { min: 100000, max: 5000000 }),
      f('employees', 'Employees', 'number', 'standard', { min: 10, max: 5000 }),
      f('tier', 'Account Tier', 'select', 'standard', { values: ['Enterprise', 'Mid-Market', 'SMB'] }),
      ownerField(),
      f('region', 'Region', 'select', 'custom', { values: REGIONS }),
      createdField(), activityField(),
    ],
  },
  {
    key: 'tasks', label: 'Tasks', icon: CheckSquare, category: 'Activities',
    description: 'Follow-ups and to-dos by owner, priority and status.', nameField: 'subject',
    fields: [
      f('subject', 'Subject', 'text', 'standard'),
      f('status', 'Status', 'select', 'standard', { values: ['Open', 'In Progress', 'Completed', 'Deferred'] }),
      f('priority', 'Priority', 'select', 'standard', { values: PRIORITIES }),
      ownerField(),
      f('related_to', 'Related To', 'select', 'related', { values: ['Deal', 'Lead', 'Contact', 'Company'] }),
      f('due_date', 'Due Date', 'date', 'standard'),
      createdField(),
    ],
  },
  {
    key: 'calls', label: 'Calls', icon: Phone, category: 'Communication',
    description: 'Logged inbound and outbound calls with duration and outcome.', nameField: 'subject',
    fields: [
      f('subject', 'Subject', 'text', 'standard'),
      f('direction', 'Direction', 'select', 'standard', { values: ['Inbound', 'Outbound'] }),
      f('outcome', 'Outcome', 'select', 'standard', { values: ['Connected', 'Voicemail', 'No Answer', 'Busy'] }),
      f('duration', 'Duration (min)', 'number', 'standard', { min: 1, max: 60 }),
      ownerField(),
      createdField(),
    ],
  },
  {
    key: 'meetings', label: 'Meetings', icon: CalendarDays, category: 'Activities',
    description: 'Scheduled meetings, demos and discovery calls.', nameField: 'subject',
    fields: [
      f('subject', 'Subject', 'text', 'standard'),
      f('meeting_type', 'Type', 'select', 'standard', { values: ['Discovery', 'Demo', 'Negotiation', 'Check-in', 'QBR'] }),
      f('status', 'Status', 'select', 'standard', { values: ['Scheduled', 'Completed', 'Cancelled', 'No Show'] }),
      f('duration', 'Duration (min)', 'number', 'standard', { min: 15, max: 120 }),
      ownerField(),
      f('meeting_date', 'Meeting Date', 'date', 'standard'),
      createdField(),
    ],
  },
  {
    key: 'invoices', label: 'Invoices', icon: Receipt, category: 'Finance',
    description: 'Billing documents with amounts, status and payment terms.', nameField: 'invoice_number',
    fields: [
      f('invoice_number', 'Invoice #', 'text', 'standard'),
      f('company', 'Company', 'text', 'related', { values: COMPANIES }),
      f('amount', 'Amount', 'currency', 'standard', { min: 500, max: 120000 }),
      f('status', 'Status', 'select', 'standard', { values: ['Draft', 'Sent', 'Paid', 'Overdue', 'Void'] }),
      f('tax', 'Tax', 'currency', 'formula', { min: 50, max: 12000 }),
      ownerField(),
      f('issue_date', 'Issue Date', 'date', 'standard'),
      f('due_date', 'Due Date', 'date', 'standard'),
    ],
  },
  {
    key: 'tickets', label: 'Tickets', icon: LifeBuoy, category: 'Support',
    description: 'Customer support cases with priority, status and SLA.', nameField: 'subject',
    fields: [
      f('subject', 'Subject', 'text', 'standard'),
      f('company', 'Company', 'text', 'related', { values: COMPANIES }),
      f('priority', 'Priority', 'select', 'standard', { values: PRIORITIES }),
      f('status', 'Status', 'select', 'standard', { values: ['New', 'Open', 'Pending', 'Resolved', 'Closed'] }),
      f('channel', 'Channel', 'select', 'standard', { values: ['Email', 'Phone', 'Chat', 'Portal'] }),
      f('csat', 'CSAT Score', 'number', 'standard', { min: 1, max: 5 }),
      ownerField(),
      createdField(),
    ],
  },
  {
    key: 'campaigns', label: 'Campaigns', icon: Megaphone, category: 'Marketing',
    description: 'Marketing campaigns with spend, leads generated and ROI.', nameField: 'campaign_name',
    fields: [
      f('campaign_name', 'Campaign Name', 'text', 'standard'),
      f('channel', 'Channel', 'select', 'standard', { values: ['Email', 'Paid Search', 'Social', 'Webinar', 'Content', 'Events'] }),
      f('status', 'Status', 'select', 'standard', { values: ['Planned', 'Active', 'Completed', 'Paused'] }),
      f('budget', 'Budget', 'currency', 'standard', { min: 5000, max: 100000 }),
      f('leads_generated', 'Leads Generated', 'number', 'standard', { min: 10, max: 800 }),
      f('roi', 'ROI', 'percent', 'formula', { min: -20, max: 320 }),
      ownerField(),
      f('start_date', 'Start Date', 'date', 'standard'),
    ],
  },
];

export const getObject = (key) => REPORT_OBJECTS.find((o) => o.key === key) || null;

export const getField = (objectKey, fieldKey) => {
  const obj = getObject(objectKey);
  return obj ? obj.fields.find((fl) => fl.key === fieldKey) || null : null;
};

export const getFieldMap = (objectKey) => {
  const obj = getObject(objectKey);
  if (!obj) return {};
  return obj.fields.reduce((acc, fl) => { acc[fl.key] = fl; return acc; }, {});
};

